<template>
	<div class="factoids-page">
		<div class="factoids-content">
			<!-- Sources Table -->
			<admin-table
				ref="sourcesTable"
				title="Sources"
				icon="📍"
				:columns="sourceColumns"
				:data="sources"
				:loading="sourcesLoading"
				:searchable="true"
				search-placeholder="Search sources..."
				:collapsible="true"
				:default-expanded="true"
				:editable="true"
				:selectable="true"
				:deletable="true"
				:addable="true"
				empty-message="No sources configured. Add one below to start scraping factoids."
				@row-select="selectSource"
				@row-edit-save="handleSourceEditSave"
				@row-delete="handleSourceDelete"
				@row-add="handleSourceAdd"
			>
				<!-- Custom action: Test button -->
				<!-- <template #custom-actions="{ row }">
					<button class="action-btn test" @click.stop="testExistingSource(row)" title="Test">🧪</button>
				</template> -->
			</admin-table>

			<!-- Factoids Table -->
			<admin-table
				ref="factiodsTable"
				title="Factoids"
				icon="📍"
				:columns="factoidColumns"
				:data="filteredFactoids"
				:loading="factoidLoading"
				:searchable="true"
				search-placeholder="Search factoids..."
				:show-clear-button="!!selectedSourceId"
				:collapsible="true"
				:default-expanded="true"
				:editable="false"
				:selectable="false"
				:deletable="true"
				:addable="true"
				:empty-message="selectedSourceId ? 'No factoids found for this source.' : 'No factoids found. Start by creating a new factoid.'"
				:badge="selectedSourceId || factoidSearch ? filteredFactoids.length + ' results' : null"
				:external-search-query="factoidSearch"
				@row-delete="handleFactoidDelete"
				@row-add="handleFactoidAdd"
				@search-update="factoidSearch = $event"
				@search-clear="handleFactoidSearchClear"
			>
				<!-- Custom cell for name -->
				<template #cell-name="{ row }">
					<span class="cell-value">{{ row.name }}</span>
				</template>
				<!-- Custom cell for source to show page URL -->
				<template #cell-source="{ value }">
					<span class="cell-value" :title="value || ''">{{ value || '—' }}</span>
				</template>
			</admin-table>
		</div>
	</div>

	<!-- Validation Toast -->
	<v-snackbar
		v-model="showValidationToast"
		:timeout="4000"
		location="bottom"
		color="error"
	>
		{{ validationMessage }}
		<template #actions>
			<v-btn
				icon="mdi-close"
				size="small"
				@click="showValidationToast = false"
			></v-btn>
		</template>
	</v-snackbar>

	<!-- Test Source Dialog -->
	<MGDialog
		v-model="showTestDialog"
		title="Test Source Preview"
		max-width="600px"
		contentHeight="400px"
		:persistent="true"
	>
		<template #content>
			<div v-if="testLoading" class="test-loading">
				<p>Loading page content...</p>
			</div>
			<div v-else-if="testError" class="test-error">
				<p><strong>Error:</strong> {{ testError }}</p>
			</div>
			<div v-else-if="testResponse" class="test-response">
				<div class="response-preview">
					<h3>Page Content Preview:</h3>
					<pre>{{ testResponse }}</pre>
				</div>
			</div>
			<div v-else class="test-empty">
				<p>No response yet.</p>
			</div>
		</template>
		<template #actions>
			<button class="action-btn primary" @click="showTestDialog = false">Close</button>
		</template>
	</MGDialog>

	<!-- Delete Source Confirmation Dialog -->
	<MGDialog
		v-model="showDeleteSourceDialog"
		title="Delete Source"
		max-width="300px"
		contentHeight="150px"
		:persistent="true"
	>
		<template #content>
			<p>Are you sure you want to delete this source? This action cannot be undone.</p>
		</template>
		<template #actions>
			<MGButton text="Delete" @click="confirmDeleteSource" :stacked="true"/>
			<MGButton text="Cancel" :outline="true" @click="cancelDeleteSource" :stacked="true"/>
		</template>
	</MGDialog>

	<!-- Delete Factoid Confirmation Dialog -->
	<MGDialog
		v-model="showDeleteFactoidDialog"
		title="Delete Factoid"
		max-width="300px"
		contentHeight="150px"
		:persistent="true"
	>
		<template #content>
			<p>Are you sure you want to delete this factoid? This action cannot be undone.</p>
		</template>
		<template #actions>
			<MGButton text="Delete" @click="confirmDeleteFactoid" :stacked="true" />
			<MGButton text="Cancel" :outline="true" @click="cancelDeleteFactoid" :stacked="true" />
		</template>
	</MGDialog>
</template>

<script>
import AdminServices from '@/admin/mixins/AdminServices';
import MGDialog from '@/components/elements/MGDialog.vue';
import MGButton from '@/components/elements/MGButton.vue';
import AdminTable from '@/admin/components/AdminTable.vue';

export default {
	name: 'FactoidsPage',
	components: { MGDialog, MGButton, AdminTable },
	mixins: [AdminServices],
	data() {
		return {
			showValidationToast: false,
			validationMessage: '',
			showTestDialog: false,
			testResponse: null,
			testLoading: false,
			testError: null,
			selectedSourceId: null,
			factoidSearch: '',
			showDeleteSourceDialog: false,
			deleteSourceId: null,
			showDeleteFactoidDialog: false,
			deleteFactoidId: null,
			// Column definitions for sources table
			sourceColumns: [
				{
					field: 'pageUrl',
					label: 'Page URL',
					width: '2fr',
					sortable: false,
					editable: true,
					type: 'url',
					required: true,
					placeholder: 'https://example.com'
				},
				{
					field: 'acceptCookiesButton',
					label: 'Accept Cookies Button',
					width: '1.5fr',
					sortable: false,
					editable: true,
					type: 'text',
					required: false,
					placeholder: 'e.g., .cookie-accept'
				},
				{
					field: 'contentSelector',
					label: 'Content Selector',
					width: '1.5fr',
					sortable: false,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'e.g., .article-content'
				},
				{
					field: 'lastScanDateTime',
					label: 'Last Scan',
					width: '1fr',
					sortable: false,
					editable: false,
					type: 'date',
					addRowDefault: '—'
				}
			],
			// Column definitions for factoids table
			factoidColumns: [
				{
					field: 'name',
					label: 'Name',
					width: '2fr',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'e.g., Interest Rate'
				},
				{
					field: 'description',
					label: 'Description',
					width: '1.5fr',
					sortable: false,
					editable: true,
					type: 'textarea',
					required: false,
					placeholder: 'e.g., Current federal interest rate'
				},
				{
					field: 'value',
					label: 'Value',
					width: '1.5fr',
					sortable: true,
					editable: true,
					type: 'text',
					required: true,
					placeholder: 'e.g., 3.5%'
				},
				{
					field: 'source',
					label: 'Source',
					width: '1.5fr',
					sortable: true,
					editable: false,
					type: 'text',
					addRowDefault: 'manual'
				},
				{
					field: 'createdDateTime',
					label: 'Created',
					width: '1.5fr',
					sortable: true,
					editable: false,
					type: 'date'
				}
			]
		};
	},
	computed: {
		filteredFactoids() {
			let filtered = this.factoids;

			// Filter by source if one is selected
			if (this.selectedSourceId) {
				const selectedSource = this.sources.find(s => s.id === this.selectedSourceId);
				if (selectedSource) {
					filtered = filtered.filter(factoid => factoid.source === selectedSource.pageUrl);
				}
			}

			// Note: search filtering is now handled by AdminTable component
			return filtered;
		}
	},
	methods: {
		selectSource(row) {
			// Toggle selection
			this.selectedSourceId = this.selectedSourceId === row.id ? null : row.id;

			// Auto-scroll to factoids section when a source is selected
			if (this.selectedSourceId) {
				this.$nextTick(() => {
					// Scroll to factoids table
					this.$refs.factiodsTable?.$el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
				});
			}
		},
		handleSourceEditSave({ rowId, data }) {
			// Validation
			if (!data.pageUrl || !data.contentSelector) {
				const missing = [];
				if (!data.pageUrl) missing.push('Page URL');
				if (!data.contentSelector) missing.push('Content Selector');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}

			// Update source via AdminServices mixin
			this._updateSource(rowId, {
				pageUrl: data.pageUrl,
				acceptCookiesButton: data.acceptCookiesButton || '',
				contentSelector: data.contentSelector
			}, () => {
				// On success, cancel edit mode in the table
				this.$refs.sourcesTable?.cancelEdit();
			});
		},
		handleSourceDelete(row) {
			this.deleteSourceId = row.id;
			this.showDeleteSourceDialog = true;
		},
		confirmDeleteSource() {
			this._deleteSource(this.deleteSourceId);
			this.showDeleteSourceDialog = false;
			this.deleteSourceId = null;
		},
		cancelDeleteSource() {
			this.showDeleteSourceDialog = false;
			this.deleteSourceId = null;
		},
		handleSourceAdd(data) {
			// Validation
			if (!data.pageUrl || !data.contentSelector) {
				const missing = [];
				if (!data.pageUrl) missing.push('Page URL');
				if (!data.contentSelector) missing.push('Content Selector');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}

			// Create new source via AdminServices mixin
			this._createSource({
				pageUrl: data.pageUrl,
				acceptCookiesButton: data.acceptCookiesButton || '',
				contentSelector: data.contentSelector
			});
		},
		testExistingSource(source) {
			this.showTestDialog = true;
			this.testLoading = true;
			this.testError = null;
			this.testResponse = null;

			// Call the service method with existing source data
			this._testSource({
				pageUrl: source.pageUrl,
				acceptCookiesButton: source.acceptCookiesButton || '',
				contentSelector: source.contentSelector
			},
				(response) => {
					this.testResponse = response;
					this.testLoading = false;
				},
				(error) => {
					this.testError = error;
					this.testLoading = false;
				}
			);
		},
		handleFactoidDelete(row) {
			this.deleteFactoidId = row.id;
			this.showDeleteFactoidDialog = true;
		},
		confirmDeleteFactoid() {
			this._deleteFactoid(this.deleteFactoidId);
			this.showDeleteFactoidDialog = false;
			this.deleteFactoidId = null;
		},
		cancelDeleteFactoid() {
			this.showDeleteFactoidDialog = false;
			this.deleteFactoidId = null;
		},
		handleFactoidAdd(data) {
			// Validation
			if (!data.name || !data.value) {
				const missing = [];
				if (!data.name) missing.push('Name');
				if (!data.value) missing.push('Value');
				this.validationMessage = `Please fill in: ${missing.join(' and ')}`;
				this.showValidationToast = true;
				return;
			}

			// Create new factoid with 'manual' source
			this._createFactoid({
				name: data.name,
				value: data.value,
				description: data.description || '',
				source: 'manual'
			});
		},
		handleFactoidSearchClear() {
			this.selectedSourceId = null;
			this.factoidSearch = '';
		}
	},
	mounted() {
		console.log('Factoids Page loaded');
		this._getFactoids();
		this._getSources();
	}
};
</script>

<style scoped>
.factoids-page {
	min-height: 100%;
}

.factoids-content {
	width: 100%;
}

/* Custom cell styling for factoid name */
.factoid-name-cell {
	display: flex;
	flex-direction: column;
	gap: 4px;
	width: 100%;
	min-width: 0;
	overflow: hidden;
}

.factoid-name-cell strong {
	color: var(--admin-text-primary);
	font-size: 14px;
	margin: 0;
	transition: color 0.3s ease;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.factoid-name-cell .description {
	color: var(--admin-text-secondary);
	font-size: 12px;
	margin: 0;
	font-weight: normal;
	transition: color 0.3s ease;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

/* Mobile alignment */
@media (max-width: 480px) {
	.factoid-name-cell {
		text-align: left;
		align-items: flex-start !important;
		align-self: flex-start !important;
		width: 100%;
	}

	.factoid-name-cell strong,
	.factoid-name-cell .description {
		text-align: left;
		align-self: flex-start;
		width: 100%;
	}
}

/* Test Dialog Styles */
.test-loading,
.test-error,
.test-empty {
	padding: 20px;
	text-align: center;
	color: var(--admin-text-secondary);
}

.test-error {
	color: #d32f2f;
	background-color: rgba(211, 47, 47, 0.05);
	border-radius: var(--border-radius-s);
}

.test-response {
	padding: 20px;
}

.response-preview {
	max-height: 100%;
	overflow-y: auto;
}

.response-preview h3 {
	margin-top: 0;
	color: var(--admin-text-primary);
	font-size: 14px;
}

.response-preview pre {
	background-color: var(--admin-hover-bg);
	padding: 12px;
	border-radius: var(--border-radius-s);
	overflow-x: auto;
	font-size: 12px;
	line-height: 1.4;
	color: var(--admin-text-primary);
	margin: 0;
	white-space: pre-wrap;
	word-break: break-word;
}

/* Test button styling */
.action-btn.test {
	color: #1e88e5;
}

.action-btn.test:hover {
	background-color: rgba(30, 136, 229, 0.1);
}

/* Snackbar positioning - much lower on screen */
:deep(.v-snackbar) {
	bottom: 100px !important;
	top: auto !important;
	margin-top: 0 !important;
}
</style>
